import React, { useMemo, useState } from 'react';
import { Alert } from 'antd';
import { Button, Modal } from '../../design-system';
import {
  AutoCorrelationStep,
  useAutoCorrelation
} from '../../providers/AutoCorrelation';
import { useSelected } from '../../providers/selectedContext';
import {
  FAMILY_KEYS,
  TENDENCY_LABELS,
  countStagedWarnings,
  parameterLabel
} from './autoCorrelationUtils';
import ScopeStep from './ScopeStep';
import RunStep from './RunStep';
import ReviewStep from './ReviewStep';
import styles from './AutoCorrelation.module.css';

const STEPS: { key: AutoCorrelationStep; label: string }[] = [
  { key: 'scope', label: '1. Scope' },
  { key: 'run', label: '2. Run' },
  { key: 'review', label: '3. Review' }
];

/**
 * Auto Correlation tab panel — Scope → Run → Review, rendered inline on the
 * Correlation page. Runs the automatic correlation as a preview and only
 * mutates the project when the user applies the (possibly overridden)
 * recommendation. The apply confirmation stays a small dialog.
 */
const AutoCorrelationPanel = () => {
  const {
    step,
    setStep,
    report,
    loading,
    error,
    overrides,
    stagedSelection,
    scopeSampleUids,
    run,
    apply,
    restoreLatest
  } = useAutoCorrelation();
  const { selectedInfo } = useSelected();

  const [confirmOpen, setConfirmOpen] = useState(false);

  const zones = selectedInfo.project?.correlation?.zones ?? [];

  const hasScope = FAMILY_KEYS.some(
    family => scopeSampleUids[family].length > 0
  );

  const overrideEntries = useMemo(
    () => Object.entries(overrides),
    [overrides]
  );

  const warningCount = useMemo(
    () => (report ? countStagedWarnings(report, stagedSelection) : 0),
    [report, stagedSelection]
  );

  // Zones are a precondition for the whole workflow: guide inline instead of
  // disabling the tab, so the feature stays discoverable.
  if (zones.length === 0) {
    return (
      <div className={styles.panelEmptyState} data-testid="auto-no-zones">
        <p className={styles.parameterName}>Create flow-unit zones first</p>
        <p className={styles.assistedHint}>
          The Auto Correlation needs flow-unit zones to simulate and validate
          the recommended curves. Create zones from the Manual Correlation tab
          (Zones button), then come back here.
        </p>
      </div>
    );
  }

  const handleApply = async () => {
    setConfirmOpen(false);
    await apply();
  };

  return (
    <div className={styles.panelRoot}>
      <div className={styles.panelScroll}>
        <div className={styles.stepIndicator}>
          {STEPS.map((entry, index) => (
            <React.Fragment key={entry.key}>
              {index > 0 ? (
                <span className={styles.stepArrow} aria-hidden="true">
                  →
                </span>
              ) : null}
              <span
                className={`${styles.stepDot} ${
                  entry.key === step ? styles.stepDotActive : ''
                }`}
              >
                {entry.label}
              </span>
            </React.Fragment>
          ))}
        </div>

        <p className={styles.assistedHint}>
          The automatic correlation suggests a fit function per parameter —
          review the suggestions and override any of them before applying.
        </p>

        {error ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: '0.75rem' }}
            message={error}
          />
        ) : null}

        {step === 'scope' ? <ScopeStep /> : null}
        {step === 'run' ? <RunStep /> : null}
        {step === 'review' ? <ReviewStep /> : null}
      </div>

      <div className={styles.actionBar}>
        {step === 'scope' && report ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setStep('review')}
            data-testid="auto-view-last-report"
          >
            View last report
          </Button>
        ) : null}
        {step === 'scope' && !report ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={restoreLatest}
            disabled={loading}
          >
            Restore last report
          </Button>
        ) : null}
        {step !== 'scope' ? (
          <Button
            variant="secondary"
            onClick={() => setStep(step === 'review' ? 'run' : 'scope')}
            disabled={loading}
          >
            Back
          </Button>
        ) : null}
        <div className={styles.footerSpacer} />
        {step === 'scope' ? (
          <Button
            variant="primary"
            onClick={() => setStep('run')}
            disabled={!hasScope}
          >
            Next
          </Button>
        ) : null}
        {step === 'run' ? (
          <Button variant="primary" onClick={run} disabled={loading}>
            {loading ? 'Running…' : 'Run Auto Correlation'}
          </Button>
        ) : null}
        {step === 'review' ? (
          <Button
            variant="primary"
            onClick={() => setConfirmOpen(true)}
            disabled={loading || !report}
            data-testid="auto-apply"
          >
            Apply…
          </Button>
        ) : null}
      </div>

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        size="sm"
        title="Apply auto correlation?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleApply}
              disabled={loading}
              data-testid="auto-confirm-apply"
            >
              Apply
            </Button>
          </>
        }
      >
        <p>
          This applies the recommended functions to the project and re-runs
          the correlation for the report&apos;s sample scope — exactly as if
          every selection had been made manually. This is an assisted
          recommendation; expert review is expected.
        </p>
        {overrideEntries.length > 0 ? (
          <>
            <p style={{ marginBottom: '0.25rem' }}>
              <strong>Your overrides:</strong>
            </p>
            <ul className={styles.overrideList}>
              {overrideEntries.map(([parameter, type]) => (
                <li key={parameter}>
                  {parameterLabel(parameter)}: {TENDENCY_LABELS[type]}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p>No manual overrides — the recommendation is applied as-is.</p>
        )}
        <p>
          {warningCount > 0
            ? `${warningCount} warning${
                warningCount === 1 ? '' : 's'
              } will be carried into this selection.`
            : 'No warnings on the staged selection.'}
        </p>
      </Modal>
    </div>
  );
};

export default AutoCorrelationPanel;
